/**
 * Push notification facade — picks the right transport at runtime.
 *
 *   Native (Android/iOS) → push.native.js  (Capacitor PushNotifications)
 *   Browser              → push.web.js     (Firebase Messaging Web SDK)
 *
 * Callers use `push.init({ onMessage })` and don't care which transport
 * is active.
 */

import { createLogger } from '../../core/logger.js';
import * as nativeImpl from './push.native.js';
import * as webImpl from './push.web.js';

const log = createLogger('push');

let initialised = false;

/**
 * @typedef {Object} PushHooks
 * @property {(notification: { data?: any; notification?: any; tapped?: boolean }) => void} [onMessage]
 */

/**
 * Initialise the appropriate transport. Safe to call multiple times.
 * Resolves with `true` if a transport registered successfully.
 *
 * @param {PushHooks} [hooks]
 * @returns {Promise<boolean>}
 */
export async function init(hooks = {}) {
  if (initialised) return true;
  initialised = true;

  if (nativeImpl.isAvailable()) {
    log.info('using native push (Capacitor)');
    return nativeImpl.init(hooks);
  }
  if (webImpl.isAvailable()) {
    log.info('using web push (Firebase Messaging)');
    return webImpl.init(hooks);
  }
  log.info('no push transport available on this platform');
  return false;
}

/**
 * Manually persist a token. Most callers shouldn't need this — the active
 * transport saves automatically — but useful for tests / manual flows.
 *
 * @param {string} token
 * @returns {Promise<void>}
 */
export async function saveToken(token) {
  if (nativeImpl.isAvailable()) return nativeImpl.saveToken(token);
  if (webImpl.isAvailable()) return webImpl.saveToken(token);
}

/**
 * @param {any} data
 * @returns {boolean}
 */
export function shouldProcess(data) {
  // Native uses an internal dedup window; for the web transport we share the
  // same module-level Set.
  return nativeImpl.shouldProcess(data);
}

/**
 * iOS PWA-specific: request notification permission + register token from
 * within a USER GESTURE handler. Required because iOS standalone PWAs
 * silently no-op on `Notification.requestPermission()` outside a tap.
 *
 * Returns:
 *   'granted'      — permission given, token registered
 *   'denied'       — user dismissed/denied
 *   'unavailable'  — no service worker / Notification API
 *
 * @returns {Promise<'granted' | 'denied' | 'unavailable'>}
 */
export async function requestPermissionAndRegister() {
  if (webImpl.isAvailable() && typeof webImpl.requestPermissionAndRegister === 'function') {
    return webImpl.requestPermissionAndRegister();
  }
  // Native path doesn't need a user gesture (Capacitor handles permission
  // through its own native dialog when init runs).
  return 'unavailable';
}
